import type { AuthProjection, AuthSnapshot, AuthUserProjection } from "./auth";

export type SessionAuthInput = {
  status: AuthSnapshot["status"];
  user?: AuthUserProjection | null;
};

export type SessionAuthSource = {
  getSnapshot: () => AuthSnapshot;
  ready: () => Promise<AuthSnapshot>;
  subscribe: (listener: () => void) => () => void;
};

const sameAuthSnapshot = (left: AuthSnapshot, right: AuthSnapshot) =>
  left.status === right.status &&
  left.user?.id === right.user?.id &&
  left.user?.role === right.user?.role &&
  left.user?.companyRole === right.user?.companyRole;

export function projectSessionAuth({
  status,
  user,
}: SessionAuthInput): AuthSnapshot {
  if (status === "authenticated") {
    if (!user) return { status: "restoring" };

    return {
      status,
      user: {
        id: user.id,
        ...(user.role ? { role: user.role } : {}),
        ...(user.companyRole !== undefined
          ? { companyRole: user.companyRole }
          : {}),
      },
    };
  }

  return status === "anonymous" ? { status } : { status: "restoring" };
}

export function createSessionAuthProjection(
  source: SessionAuthSource,
): AuthProjection {
  return {
    get status() {
      return source.getSnapshot().status;
    },
    get user() {
      return source.getSnapshot().user;
    },
    ready: source.ready,
    subscribe: (listener) => {
      let previous = source.getSnapshot();
      return source.subscribe(() => {
        const next = source.getSnapshot();
        if (sameAuthSnapshot(previous, next)) return;
        previous = next;
        listener();
      });
    },
  };
}
